import React, { useState } from 'react';
import Notification from '@Common/Notification';
import RadioGroup from '@Common/RadioGroup/RadioGroup';
import Modal61 from '@Common/Modal/Modal61';
import { FEEDBACK_OPTIONS } from '@Constants/constants';
import TextArea from '@Components/Common/TextArea';
import { useCreateFeedback } from '@Apis/locationTasks';
import type { Modal61Ref } from '@Types/common';

interface FeedbackModalProps {
    selectedFeedbackOption: string;
    setSelectedFeedbackOption: (value: string) => void;
    feedbackText: string;
    setFeedbackText: (value: string) => void;
    handleCloseFeedbackModal: () => void;
    locationTaskId: number;
    feedbackModalRef: React.RefObject<Modal61Ref | null>;
    hasBaselineTaskLink?: boolean;
}

const FeedbackModal =
    ({ selectedFeedbackOption,
        setSelectedFeedbackOption,
        feedbackText,
        setFeedbackText,
        handleCloseFeedbackModal,
        locationTaskId,
        feedbackModalRef,
        hasBaselineTaskLink = false
    }: FeedbackModalProps) => {
        const feedbackMutation = useCreateFeedback((result) => {
            if (result) {
                Notification('success', 'Success', 'Thank you for your feedback!');
                handleCloseFeedbackModal();
            }
        });

        // Filter feedback options based on whether there's a baseline task link
        const filteredFeedbackOptions = FEEDBACK_OPTIONS.filter(option => {
            // Option "5" is "The link is not correct" - only show when there's a link
            if (option.id === '5') {
                return hasBaselineTaskLink;
            }
            return true;
        });

        const handleSubmitFeedback = () => {
            if (feedbackMutation.isPending) return;

            if (selectedFeedbackOption.trim() === '') {
                Notification('error', 'Error', 'Please select a valid feedback option.');
                return;
            }
            const selectedOption = filteredFeedbackOptions.find(
                option => String(option.id) === selectedFeedbackOption
            );
            if (!selectedOption) {
                Notification('error', 'Error', 'Please select a valid feedback option.');
                return;
            }
            const feedbackChoice = Number(selectedOption.id);

            const feedbackRequest = {
                locationTaskId,
                feedbackChoice,
                text: feedbackText,
            };

            feedbackMutation.mutate(feedbackRequest, {
                onError: (error) => {
                    const failureMessage = (error as Error)?.message || 'Failed to submit feedback. Please try again.';
                    Notification('error', 'Error', failureMessage);
                },
            });
        };


        return <Modal61
            ref={feedbackModalRef}
            title='Suggestions'
            size='md'
            isLoading={feedbackMutation.isPending}
            body={(
                <div style={{ padding: '20px' }}>
                    <p style={{ fontSize: '12px', marginBottom: '20px', textAlign: 'center' }}>
                        Your feedback helps keep tasks accurate. Individuals who maintain this task can update the task for future locations. If you need
                        a response, please create a Support Ticket.
                    </p>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '10px',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            textAlign: 'center'
                        }}>
                            Please select an option:
                        </label>
                        <RadioGroup
                            defaultValue=''
                            ariaLabel="Feedback options"
                            name="feedbackOptions"
                            options={filteredFeedbackOptions.map(option => ({
                                value: option.id,
                                label: option.label
                            }))}
                            value={selectedFeedbackOption}
                            onValueChange={setSelectedFeedbackOption}
                            centered={true}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <TextArea
                            rows={4}
                            name="feedbackText"
                            label="Feedback Comment"
                            value={feedbackText}
                            isRequired={false}
                            onChange={(e) => setFeedbackText(e.target.value)}
                        />
                    </div>
                </div>
            )}
            customFooter={(
                <div className='mx-auto'>
                    <button
                        type='button'
                        className='btn btn-primary me-3'
                        onClick={handleSubmitFeedback}
                        disabled={feedbackMutation.isPending}
                    >
                        {feedbackMutation.isPending ? 'Submitting...' : 'Submit'}
                    </button>
                    <button
                        type='button'
                        className='btn btn-light btn-outline-dark'
                        onClick={handleCloseFeedbackModal}
                        disabled={feedbackMutation.isPending}
                    >
                        Cancel
                    </button>
                </div>
            )}
            onCloseClick={handleCloseFeedbackModal}
        />
    };

export default FeedbackModal;
